import React, { useState, useEffect, useMemo } from 'react';
import { FiRefreshCw } from 'react-icons/fi';
import { IconType } from 'react-icons';
import { dashboardService } from '../api/services/dashboardService';
import { MetricsByCountryItem, MetricsByAgeRangeItem } from '../api/types';
import MetricsByCountryTable from '../components/dashboard/MetricsByCountryTable';
import MetricsByAgeRangeTable from '../components/dashboard/MetricsByAgeRangeTable';
import AppMetricsTableSkeleton from '../components/dashboard/skeletons/AppMetricsTableSkeleton';

const MetaAnalytics: React.FC = () => {
  const [metricsByCountry, setMetricsByCountry] = useState<MetricsByCountryItem[]>([]);
  const [metricsByAgeRange, setMetricsByAgeRange] = useState<MetricsByAgeRangeItem[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [isRefreshing, setIsRefreshing] = useState<boolean>(false);

  const renderIcon = (Icon: IconType, className: string) => {
    const IconComponent = Icon as React.ComponentType<{ className?: string }>;
    return <IconComponent className={className} />;
  };

  // Fetch metrics by country
  const fetchMetricsByCountry = async () => {
    try {
      const response = await dashboardService.getMetricsByCountry();
      setMetricsByCountry(response);
    } catch (err: any) {
      console.error('Error fetching metrics by country:', err);
    }
  };

  // Fetch metrics by age range
  const fetchMetricsByAgeRange = async () => {
    try {
      const response = await dashboardService.getMetricsByAgeRange();
      setMetricsByAgeRange(response);
    } catch (err: any) {
      console.error('Error fetching metrics by age range:', err);
    }
  };

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([
        fetchMetricsByCountry(),
        fetchMetricsByAgeRange(),
      ]);
      setLoading(false);
    };
    loadData();
  }, []);

  const handleRefresh = () => {
    setIsRefreshing(true);
    Promise.all([
      fetchMetricsByCountry(),
      fetchMetricsByAgeRange(),
    ]).finally(() => {
      setIsRefreshing(false);
    });
  };

  // Filter data by metric type
  const appSessionCountryData = useMemo(() => 
    metricsByCountry.filter(item => item.metric_type === 'app_session'),
    [metricsByCountry]
  );
  const gameSessionCountryData = useMemo(() => 
    metricsByCountry.filter(item => item.metric_type === 'game_session'),
    [metricsByCountry]
  );
  const testSessionCountryData = useMemo(() => 
    metricsByCountry.filter(item => item.metric_type === 'test_session'),
    [metricsByCountry]
  );

  const appSessionAgeData = useMemo(() => 
    metricsByAgeRange.filter(item => item.metric_type === 'app_session'),
    [metricsByAgeRange]
  );
  const gameSessionAgeData = useMemo(() => 
    metricsByAgeRange.filter(item => item.metric_type === 'game_session'),
    [metricsByAgeRange]
  );
  const testSessionAgeData = useMemo(() => 
    metricsByAgeRange.filter(item => item.metric_type === 'test_session'),
    [metricsByAgeRange]
  );

  return (
    <div className="w-full max-w-7xl mx-auto space-y-6">
      {/* Header */}
      <div className="mb-8 flex items-center justify-between">
        <div>
          <h1 className="text-3xl lg:text-4xl font-bold bg-gradient-to-r from-slate-800 to-slate-600 dark:from-slate-200 dark:to-slate-400 bg-clip-text text-transparent mb-2">
            Meta Analytics
          </h1>
          <p className="text-slate-600 dark:text-slate-400">
            Meta analitik verileri ve istatistikleri
          </p>
        </div>
        <button
          onClick={handleRefresh}
          disabled={isRefreshing || loading}
          className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-slate-700 dark:text-slate-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          {renderIcon(FiRefreshCw, `w-4 h-4 ${isRefreshing ? 'animate-spin' : ''}`)}
          <span>Yenile</span>
        </button>
      </div>

      {/* App Session Tables */}
      <div className="space-y-6">
        <div>
          <h2 className="text-2xl font-bold text-slate-800 dark:text-white mb-4 flex items-center gap-2">
            <span className="inline-block bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200 px-3 py-1 rounded font-semibold">
              App Session
            </span>
          </h2>
          <div className="space-y-6">
            {loading && appSessionCountryData.length === 0 ? (
              <AppMetricsTableSkeleton />
            ) : (
              <MetricsByCountryTable
                metricsData={appSessionCountryData}
                loading={loading}
                hideMetricTypeFilter={true}
              />
            )}
            {loading && appSessionAgeData.length === 0 ? (
              <AppMetricsTableSkeleton />
            ) : (
              <MetricsByAgeRangeTable
                metricsData={appSessionAgeData}
                loading={loading}
                hideMetricTypeFilter={true}
              />
            )}
          </div>
        </div>

        {/* Game Session Tables */}
        <div>
          <h2 className="text-2xl font-bold text-slate-800 dark:text-white mb-4 flex items-center gap-2">
            <span className="inline-block bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200 px-3 py-1 rounded font-semibold">
              Game Session
            </span>
          </h2>
          <div className="space-y-6">
            {loading && gameSessionCountryData.length === 0 ? (
              <AppMetricsTableSkeleton />
            ) : (
              <MetricsByCountryTable
                metricsData={gameSessionCountryData}
                loading={loading}
                hideMetricTypeFilter={true}
              />
            )}
            {loading && gameSessionAgeData.length === 0 ? (
              <AppMetricsTableSkeleton />
            ) : (
              <MetricsByAgeRangeTable
                metricsData={gameSessionAgeData}
                loading={loading}
                hideMetricTypeFilter={true}
              />
            )}
          </div>
        </div>

        {/* Test Session Tables */}
        <div>
          <h2 className="text-2xl font-bold text-slate-800 dark:text-white mb-4 flex items-center gap-2">
            <span className="inline-block bg-purple-100 dark:bg-purple-900 text-purple-800 dark:text-purple-200 px-3 py-1 rounded font-semibold">
              Test Session
            </span>
          </h2>
          <div className="space-y-6">
            {loading && testSessionCountryData.length === 0 ? (
              <AppMetricsTableSkeleton />
            ) : (
              <MetricsByCountryTable
                metricsData={testSessionCountryData}
                loading={loading}
                hideMetricTypeFilter={true}
              />
            )}
            {loading && testSessionAgeData.length === 0 ? (
              <AppMetricsTableSkeleton />
            ) : (
              <MetricsByAgeRangeTable
                metricsData={testSessionAgeData}
                loading={loading}
                hideMetricTypeFilter={true}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default MetaAnalytics;
